// src/components/Orders/Admin/DesktopOrdersView.js
import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Select, MenuItem, Chip, Button, useTheme
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import Info from '@mui/icons-material/Info';

const DesktopOrdersView = ({ orders, handleStatusUpdate, getStatusColor, getStatusLabel }) => {
  const navigate = useNavigate();
  const theme = useTheme();

  const getStatusThemeColor = (status) => {
    switch (status) {
      case 'delivered':
        return theme.palette.success.main;
      case 'cancelled':
        return theme.palette.error.main;
      case 'confirmed':
        return theme.palette.primary.main;
      case 'shipped':
        return theme.palette.info.main;
      default:
        return theme.palette.warning.main; // For 'pending'
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <AccessTimeIcon sx={{ fontSize: 16 }} />;
      case 'confirmed':
        return <ThumbUpIcon sx={{ fontSize: 16 }} />;
      case 'shipped':
        return <LocalShippingIcon sx={{ fontSize: 16 }} />;
      case 'delivered':
        return <CheckCircleOutlineIcon sx={{ fontSize: 16 }} />;
      case 'cancelled':
        return <HighlightOffIcon sx={{ fontSize: 16 }} />;
      default:
        return null;
    }
  };

  return (
    <TableContainer 
      sx={{ 
        direction: 'rtl',
        '& .MuiTable-root': {
          direction: 'rtl',
          minWidth: 650,
          borderCollapse: 'separate',
          borderSpacing: 0
        },
        // Enhanced mobile responsiveness
        '@media (max-width: 900px)': {
          '& .MuiTable-root': {
            minWidth: 'auto'
          }
        },
        // Horizontal scroll on very small screens if needed
        overflowX: 'auto'
      }}
    >
      <Table sx={{ direction: 'rtl' }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{
              textAlign: 'center',
              direction: 'rtl',
              padding: '16px 24px',
              fontWeight: 'bold',
              color: 'white',
              backgroundColor: 'primary.main',
              borderTopRightRadius: '8px',
              '@media (max-width: 900px)': { padding: '12px 8px', fontSize: '0.875rem' },
              '@media (max-width: 600px)': { padding: '8px 4px', fontSize: '0.8rem' }
            }}>
              رقم الطلب
            </TableCell>
            <TableCell sx={{
              textAlign: 'center',
              direction: 'rtl',
              padding: '16px 24px',
              fontWeight: 'bold',
              color: 'white',
              backgroundColor: 'primary.main',
              borderRight: '1px solid rgba(255, 255, 255, 0.2)',
              '@media (max-width: 900px)': { padding: '12px 8px', fontSize: '0.875rem' },
              '@media (max-width: 600px)': { padding: '8px 4px', fontSize: '0.8rem' }
            }}>
              العميل
            </TableCell>
            <TableCell sx={{
              textAlign: 'center',
              direction: 'rtl',
              padding: '16px 24px',
              fontWeight: 'bold',
              color: 'white',
              backgroundColor: 'primary.main',
              borderRight: '1px solid rgba(255, 255, 255, 0.2)',
              '@media (max-width: 900px)': { padding: '12px 8px', fontSize: '0.875rem' },
              '@media (max-width: 600px)': { padding: '8px 4px', fontSize: '0.8rem' }
            }}>
              التاريخ
            </TableCell>
            <TableCell sx={{
              textAlign: 'center',
              direction: 'rtl',
              padding: '16px 24px',
              fontWeight: 'bold',
              color: 'white',
              backgroundColor: 'primary.main',
              borderRight: '1px solid rgba(255, 255, 255, 0.2)',
              '@media (max-width: 900px)': { padding: '12px 8px', fontSize: '0.875rem' },
              '@media (max-width: 600px)': { padding: '8px 4px', fontSize: '0.8rem' }
            }}>
              المبلغ
            </TableCell>
            <TableCell sx={{
              textAlign: 'center',
              direction: 'rtl',
              padding: '16px 24px',
              fontWeight: 'bold',
              color: 'white',
              backgroundColor: 'primary.main',
              borderRight: '1px solid rgba(255, 255, 255, 0.2)',
              '@media (max-width: 900px)': { padding: '12px 8px', fontSize: '0.875rem' },
              '@media (max-width: 600px)': { padding: '8px 4px', fontSize: '0.8rem' }
            }}>
              الحالة
            </TableCell>
            <TableCell sx={{
              textAlign: 'center',
              direction: 'rtl',
              padding: '16px 24px',
              fontWeight: 'bold',
              color: 'white',
              backgroundColor: 'primary.main',
              borderRight: '1px solid rgba(255, 255, 255, 0.2)',
              '@media (max-width: 900px)': { padding: '12px 8px', fontSize: '0.875rem' },
              '@media (max-width: 600px)': { padding: '8px 4px', fontSize: '0.8rem' }
            }}>
              تحديث الحالة
            </TableCell>
            <TableCell sx={{
              textAlign: 'center',
              direction: 'rtl',
              padding: '16px 24px',
              fontWeight: 'bold',
              color: 'white',
              backgroundColor: 'primary.main',
              borderTopLeftRadius: '8px',
              borderRight: '1px solid rgba(255, 255, 255, 0.2)',
              '@media (max-width: 900px)': { padding: '12px 8px', fontSize: '0.875rem' },
              '@media (max-width: 600px)': { padding: '8px 4px', fontSize: '0.8rem' }
            }}>
              التفاصيل
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {orders.map((order, idx) => (
            <TableRow key={order.id} hover>
              <TableCell sx={{
                textAlign: 'center',
                direction: 'rtl',
                padding: '16px 24px',
                '@media (max-width: 900px)': { padding: '12px 8px', fontSize: '0.875rem' },
                '@media (max-width: 600px)': { padding: '8px 4px', fontSize: '0.8rem' }
              }}>
                <Typography variant="body2" sx={{ 
                  fontWeight: 600,
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  color: 'primary.main'
                }}>
                  #{order.id.slice(0, 8)}
                </Typography>
              </TableCell>
              <TableCell sx={{
                textAlign: 'center',
                direction: 'rtl',
                padding: '16px 24px',
                borderRight: '1px solid rgba(224, 224, 224, 1)',
                '@media (max-width: 900px)': { padding: '12px 8px', fontSize: '0.875rem' },
                '@media (max-width: 600px)': { padding: '8px 4px', fontSize: '0.8rem' }
              }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                  <Typography variant="body2" sx={{ 
                    fontWeight: 600,
                    fontSize: { xs: '0.75rem', sm: '0.875rem' }
                  }}>
                    {order.profiles?.full_name || 'غير محدد'}
                  </Typography>
                  <Typography variant="caption" sx={{ 
                    color: 'text.secondary',
                    fontSize: { xs: '0.65rem', sm: '0.75rem' }
                  }}>
                    {order.profiles?.phone_number || 'غير محدد'}
                  </Typography>
                </Box>
              </TableCell>
              <TableCell sx={{
                textAlign: 'center',
                direction: 'rtl',
                padding: '16px 24px',
                borderRight: '1px solid rgba(224, 224, 224, 1)',
                '@media (max-width: 900px)': { padding: '12px 8px', fontSize: '0.875rem' },
                '@media (max-width: 600px)': { padding: '8px 4px', fontSize: '0.8rem' }
              }}>
                <Typography variant="body2" sx={{ 
                  fontWeight: 'bold',
                  fontSize: { xs: '0.75rem', sm: '0.875rem' }
                }}>
                  {new Date(order.created_at).toLocaleDateString('ar-EG', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })}
                </Typography>
              </TableCell>
              <TableCell sx={{
                textAlign: 'center',
                direction: 'rtl',
                padding: '16px 24px',
                borderRight: '1px solid rgba(224, 224, 224, 1)',
                '@media (max-width: 900px)': { padding: '12px 8px', fontSize: '0.875rem' },
                '@media (max-width: 600px)': { padding: '8px 4px', fontSize: '0.8rem' }
              }}>
                <Typography variant="body2" sx={{ 
                  fontWeight: 700,
                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                  color: 'success.main'
                }}>
                  {order.total_amount.toFixed(2)} جنيه
                </Typography>
              </TableCell>
              <TableCell sx={{
                textAlign: 'center',
                direction: 'rtl',
                padding: '16px 24px',
                borderRight: '1px solid rgba(224, 224, 224, 1)',
                '@media (max-width: 900px)': { padding: '12px 8px', fontSize: '0.875rem' },
                '@media (max-width: 600px)': { padding: '8px 4px', fontSize: '0.8rem' }
              }}>
                <Chip
                  icon={getStatusIcon(order.status)}
                  label={getStatusLabel(order.status)}
                  color={getStatusColor(order.status)}
                  size="small"
                  sx={{ 
                    fontWeight: 'bold',
                    fontSize: { xs: '0.65rem', sm: '0.75rem' },
                    height: { xs: 20, sm: 24 },
                    minWidth: '120px',
                    '& .MuiChip-icon': {
                      color: 'inherit'
                    }
                  }}
                />
              </TableCell>
              <TableCell sx={{
                textAlign: 'center',
                direction: 'rtl',
                padding: '16px 24px',
                borderRight: '1px solid rgba(224, 224, 224, 1)',
                '@media (max-width: 900px)': { padding: '12px 8px', fontSize: '0.875rem' },
                '@media (max-width: 600px)': { padding: '8px 4px', fontSize: '0.8rem' }
              }}>
                <Select
                  value={order.status}
                  onChange={(e) => handleStatusUpdate(order.id, e.target.value)}
                  size="small"
                  sx={{
                    minWidth: { xs: 140, sm: 160, md: 180 },
                    '& .MuiSelect-select': {
                      textAlign: 'center',
                      fontWeight: 'bold',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      color: getStatusThemeColor(order.status),
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }
                  }}
                  renderValue={(value) => (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
                      {getStatusIcon(value)}
                      {getStatusLabel(value)}
                    </Box>
                  )}
                >
                  <MenuItem 
                    value="pending" 
                    sx={{ 
                      justifyContent: 'center', 
                      fontWeight: 'bold',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      color: theme.palette.warning.main,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    قيد الانتظار
                    <AccessTimeIcon sx={{ fontSize: 18 }} />
                  </MenuItem>
                  <MenuItem 
                    value="confirmed" 
                    sx={{ 
                      justifyContent: 'center', 
                      fontWeight: 'bold',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      color: theme.palette.primary.main,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    تم التأكيد
                    <ThumbUpIcon sx={{ fontSize: 18 }} />
                  </MenuItem>
                  <MenuItem 
                    value="shipped" 
                    sx={{ 
                      justifyContent: 'center', 
                      fontWeight: 'bold',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      color: theme.palette.info.main,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    تم الشحن
                    <LocalShippingIcon sx={{ fontSize: 18 }} />
                  </MenuItem>
                  <MenuItem 
                    value="delivered" 
                    sx={{ 
                      justifyContent: 'center', 
                      fontWeight: 'bold',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      color: theme.palette.success.main,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    تم التسليم
                    <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />
                  </MenuItem>
                  <MenuItem 
                    value="cancelled" 
                    sx={{ 
                      justifyContent: 'center', 
                      fontWeight: 'bold',
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      color: theme.palette.error.main,
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1
                    }}
                  >
                    ملغى
                    <HighlightOffIcon sx={{ fontSize: 18 }} />
                  </MenuItem>
                </Select>
              </TableCell>
              <TableCell sx={{
                textAlign: 'center',
                direction: 'rtl',
                padding: '16px 24px',
                borderRight: '1px solid rgba(224, 224, 224, 1)',
                '@media (max-width: 900px)': { padding: '12px 8px', fontSize: '0.875rem' },
                '@media (max-width: 600px)': { padding: '8px 4px', fontSize: '0.8rem' }
              }}>
                <Button 
                  variant="outlined" 
                  size="small"
                  onClick={() => navigate(`/admin/order/${order.id}`)}
                  startIcon={<Info />}
                  sx={{
                    borderRadius: 2,
                    fontWeight: 'bold',
                    fontSize: { xs: '0.65rem', sm: '0.75rem' },
                    minWidth: 'auto',
                    padding: '4px 12px',
                    textTransform: 'none',
                    '& .MuiButton-startIcon': {
                      marginRight: '4px',
                      marginLeft: 1
                    }
                  }}
                >
                  عرض التفاصيل
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default DesktopOrdersView;